import { useMemo, useRef } from 'react';
import { Menu, MenuItem } from '@carbon/react';
import { Copy, Launch, Location } from '@carbon/react/icons';
import useClickOutside from 'lib/hooks/useClickOutside';
import { copyCoordinates } from './hooks';
import {
  formatDecimalPair,
  googleMapsUrl,
  type LngLat,
} from './coordinate-utils';
import styles from './coordinate-pins.module.scss';

export interface MapContextMenuProps {
  /** Position in the window, in pixels: Carbon's menu sits on the page body. */
  x: number;
  y: number;
  lngLat: LngLat;
  onDropPin: () => void;
  onClose: () => void;
  /** Called once the coordinates are on the clipboard. */
  onCopied?: () => void;
  /** Called when the browser wouldn't copy them. */
  onCopyFailed?: () => void;
}

/**
 * The right-click menu at a spot, as in Google Maps: the coordinates lead with a
 * copy icon and clicking them copies, then drop a pin, then open the spot in
 * Google Maps. Carbon's menu handles focus, the arrow keys, Escape and the window's
 * edges; a click on the map, which takes no focus, closes it here.
 *
 * @param {MapContextMenuProps} props - Component props.
 * @return {JSX.Element} The menu.
 */
export function MapContextMenu({
  x,
  y,
  lngLat,
  onDropPin,
  onClose,
  onCopied,
  onCopyFailed,
}: MapContextMenuProps): JSX.Element {
  const ref = useRef<HTMLUListElement>(null);
  const refs = useMemo(() => [ref], []);
  useClickOutside({ refs, onClickOutside: onClose });

  return (
    <Menu ref={ref} open label="Map location" x={x} y={y} onClose={onClose}>
      <MenuItem
        label={formatDecimalPair(lngLat)}
        aria-label={`Copy ${formatDecimalPair(lngLat)}`}
        renderIcon={Copy}
        onClick={() => {
          void copyCoordinates(lngLat).then(
            () => onCopied?.(),
            () => onCopyFailed?.()
          );
        }}
      />
      <MenuItem
        label="Drop a pin here"
        renderIcon={Location}
        onClick={onDropPin}
      />
      <MenuItem
        label="Open in Google Maps"
        renderIcon={Launch}
        className={styles.menuLink}
        onClick={() => {
          window.open(googleMapsUrl(lngLat), '_blank', 'noopener,noreferrer');
        }}
      />
    </Menu>
  );
}
